import { useState } from "react";
import {
  Activity, ArrowRight, User as UserIcon, ChevronDown, ChevronRight,
  ChevronLeft, Calendar,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EmptyState } from "@/components/EmptyState";
import { formatDateTime, toISODate } from "@/lib/utils";
import { useAuditLog, type AuditLogEntry } from "./api";

type Props = {
  entidad: string;
  entidadId: string;
};

const ACTION_LABEL: Record<string, string> = {
  "contrato.update": "Contrato editado",
  "contrato.create": "Contrato creado",
  "contrato.suspend": "Contrato suspendido",
  "contrato.reactivate": "Contrato reactivado",
  // rc144: transiciones hechas por el worker MikroTik + re-anclaje de ciclo
  "contrato.router_error": "Router no aplicó el cambio",
  "contrato.router_ok": "Router aplicó el cambio",
  "contrato.reancla_ciclo": "Ciclo re-anclado",
  "contrato.ciclo_revision_descartada": "Revisión de ciclo descartada",
  "contrato.cancel": "Contrato cancelado",
  "contrato.regenerate_password": "Password PPPoE regenerado",
  "cliente.update": "Cliente editado",
  "cliente.create": "Cliente creado",
  "factura.create": "Factura emitida",
  "factura.anular": "Factura anulada",
  "pago.create": "Pago aplicado",
  "pago.anular": "Pago anulado",
  // rc19: planes
  "plan.create": "Plan creado",
  "plan.update": "Plan editado",
  "plan.delete": "Plan eliminado",
};

// Variantes de badge por familia de acción (consistente con /auditoria)
const ACTION_VARIANT: Record<
  string,
  "success" | "warning" | "destructive" | "secondary" | "default"
> = {
  "contrato.create": "success",
  "cliente.create": "success",
  "factura.create": "success",
  "pago.create": "success",
  "contrato.update": "default",
  "cliente.update": "default",
  "contrato.suspend": "destructive",
  "contrato.cancel": "destructive",
  "factura.anular": "destructive",
  "pago.anular": "destructive",
  "contrato.reactivate": "warning",
  "contrato.router_error": "destructive",
  "contrato.router_ok": "success",
  "contrato.reancla_ciclo": "default",
  "contrato.ciclo_revision_descartada": "secondary",
  "contrato.regenerate_password": "secondary",
  // rc19: planes
  "plan.create": "success",
  "plan.update": "default",
  "plan.delete": "destructive",
};

// Etiquetas legibles para los campos del payload
const FIELD_LABEL: Record<string, string> = {
  pppoe_user: "Usuario PPPoE",
  fecha_corte: "Día de corte",
  precio_override: "Precio override",
  descuento_pct: "% descuento",
  dias_gracia_override: "Días gracia",
  exento_facturacion: "Exento facturación",
  exento_monitoreo: "Exento monitoreo",
  estatus: "Estatus",
  notas: "Notas",
  notas_internas: "Notas internas",
  fecha_activacion: "Fecha activación",
  fecha_suspension: "Fecha suspensión",
  fecha_cancelacion: "Fecha cancelación",
  razon_social: "Razón social",
  nombre_comercial: "Nombre comercial",
  rfc: "RFC",
  tipo: "Tipo",
  password_rotated: "Password rotado",
  folio: "Folio",
  folio_factura: "Folio factura",
  folio_pago: "Folio pago",
  monto: "Monto",
  metodo: "Método",
  total: "Total",
  subtotal: "Subtotal",
  descuento_monto: "Descuento",
  saldo: "Saldo",
  "wireless.cpe_mac": "MAC CPE",
  "wireless.cpe_vendor": "Vendor CPE",
  "wireless.cpe_modelo": "Modelo CPE",
  "wireless.cpe_ip_management": "IP mgmt CPE",
  "wireless.ssid_conexion": "SSID",
  "wireless.frecuencia_mhz": "Frecuencia",
  "wireless.polarizacion": "Polarización",
  "fiber.onu_mac": "MAC ONU",
  "fiber.onu_vendor": "Vendor ONU",
  "fiber.onu_serial": "Serial ONU",
  "fiber.olt_puerto": "Puerto OLT",
  "fiber.splitter_nivel": "Splitter",
  // rc19: campos de plan + stubs en cliente
  plan_id: "Plan",
  nombre: "Nombre",
  descripcion: "Descripción",
  tipo_servicio: "Tipo de servicio",
  velocidad_down_mbps: "Velocidad bajada (Mbps)",
  velocidad_up_mbps: "Velocidad subida (Mbps)",
  precio: "Precio",
  moneda: "Moneda",
  rate_limit_profile: "Profile rate-limit",
  periodicidad_dias: "Periodicidad (días)",
  burst_down_mbps: "Burst bajada (Mbps)",
  burst_up_mbps: "Burst subida (Mbps)",
  reuso_ratio: "Ratio reuso",
  orden: "Orden",
  contrato_codigo: "Contrato",
  contrato_id: "Contrato ID",
  campos_cambiados: "Campos modificados",
};

function labelFor(field: string): string {
  return FIELD_LABEL[field] ?? field;
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "sí" : "no";
  if (typeof v === "string" && v === "") return "(vacío)";
  return String(v);
}

function extractChanges(
  antes: Record<string, unknown> | null,
  despues: Record<string, unknown> | null,
): { field: string; before: unknown; after: unknown }[] {
  const keys = new Set<string>([
    ...Object.keys(antes ?? {}),
    ...Object.keys(despues ?? {}),
  ]);
  // Excluir metadatos internos del diff
  keys.delete("__motivo__");
  keys.delete("__extra__");
  return Array.from(keys)
    .map((k) => ({
      field: k,
      before: antes?.[k],
      after: despues?.[k],
    }))
    .filter((c) => c.before !== c.after);
}

function shiftDay(iso: string | null, deltaDays: number): string {
  const d = iso ? new Date(iso + "T12:00:00") : new Date();
  d.setDate(d.getDate() + deltaDays);
  return toISODate(d);
}

/**
 * Timeline de auditoría filtrado por entidad. Usado en tab "Actividad" del
 * cliente y "Historial" del contrato.
 *
 * Features (dev66):
 *   - Filas colapsadas por default — click para expandir el diff + motivo.
 *   - Filtro por día con flechas ← → y botón "Ver todo" (mismo patrón que
 *     /auditoria global, para no saturar con historial infinito).
 */
export function AuditTimeline({ entidad, entidadId }: Props) {
  const todayISO = toISODate(new Date());
  const [fechaDia, setFechaDia] = useState<string | null>(todayISO);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const { data, isLoading } = useAuditLog({
    entidad,
    entidad_id: entidadId,
    fecha_dia: fechaDia ?? undefined,
    size: 100,
  });
  const items = data?.items ?? [];

  function toggleExpand(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const filtroActivo = fechaDia !== null;

  return (
    <div className="flex flex-col gap-3">
      {/* Controles de fecha */}
      <div className="flex flex-wrap items-center gap-1.5 rounded-lg border bg-card p-2">
        <Calendar size={12} className="ml-1 text-muted-foreground shrink-0" />
        <Button
          size="sm" variant="outline"
          disabled={!fechaDia}
          onClick={() => setFechaDia(shiftDay(fechaDia, -1))}
          title="Día anterior"
          className="h-7 w-7 p-0"
        >
          <ChevronLeft size={12} />
        </Button>
        <Input
          type="date"
          className="h-7 w-36 text-xs"
          value={fechaDia ?? ""}
          onChange={(e) => setFechaDia(e.target.value || null)}
        />
        <Button
          size="sm" variant="outline"
          disabled={!fechaDia || fechaDia >= todayISO}
          onClick={() => setFechaDia(shiftDay(fechaDia, 1))}
          title="Día siguiente"
          className="h-7 w-7 p-0"
        >
          <ChevronRight size={12} />
        </Button>
        <Button
          size="sm"
          variant={fechaDia === null ? "default" : "ghost"}
          onClick={() => setFechaDia(fechaDia ? null : todayISO)}
          className="h-7 text-[11px]"
        >
          {fechaDia === null ? "Mostrando todo" : "Ver todo"}
        </Button>
        <span className="ml-auto text-[11px] text-muted-foreground mr-1">
          {data
            ? `${data.total} evento${data.total === 1 ? "" : "s"}${filtroActivo ? ` en ${fechaDia}` : ""}`
            : ""}
        </span>
      </div>

      {/* Lista */}
      {isLoading && items.length === 0 ? (
        <div className="py-4 text-center text-xs text-muted-foreground">
          Cargando historial…
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Activity}
          title="Sin eventos"
          description={
            filtroActivo
              ? `Sin actividad en ${fechaDia}. Prueba otro día o "Ver todo".`
              : "No hay cambios registrados todavía."
          }
        />
      ) : (
        <>
          <div className="flex flex-col gap-1.5">
            {items.map((entry) => (
              <AuditEntryRow
                key={entry.id}
                entry={entry}
                expanded={expanded.has(entry.id)}
                onToggle={() => toggleExpand(entry.id)}
              />
            ))}
          </div>
          {data && data.total > items.length && (
            <p className="text-[10px] text-muted-foreground text-center">
              Mostrando {items.length} de {data.total} eventos. Navega por día
              para ver más.
            </p>
          )}
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Fila compacta — colapsada por default, expande al click
// ══════════════════════════════════════════════════════════════════════════
function AuditEntryRow({
  entry, expanded, onToggle,
}: {
  entry: AuditLogEntry;
  expanded: boolean;
  onToggle: () => void;
}) {
  const changes = extractChanges(entry.payload_antes, entry.payload_despues);
  const motivo = (entry.payload_despues?.__motivo__ as string | undefined) ?? null;
  const actionLabel = ACTION_LABEL[entry.accion] ?? entry.accion;
  const actionVariant = ACTION_VARIANT[entry.accion] ?? "default";

  // Resumen corto cuando está colapsado
  const resumen = changes.length > 0
    ? `${changes.length} campo${changes.length === 1 ? "" : "s"}`
    : null;

  return (
    <div className="rounded-md border bg-card text-xs overflow-hidden">
      {/* Fila principal clickable */}
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-accent/50 transition-colors"
      >
        <span className="shrink-0 text-muted-foreground">
          {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        </span>
        <span className="font-mono text-[11px] text-muted-foreground whitespace-nowrap shrink-0">
          {formatDateTime(entry.created_at)}
        </span>
        <Badge variant={actionVariant} className="shrink-0">
          {actionLabel}
        </Badge>
        <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground shrink-0">
          <UserIcon size={10} />
          {entry.actor_email ?? "sistema"}
        </span>
        <span className="ml-auto flex items-center gap-2 text-[11px] text-muted-foreground truncate">
          {resumen && <span>{resumen}</span>}
          {motivo && (
            <span className="italic truncate max-w-[320px]">
              {resumen && "·"} {motivo}
            </span>
          )}
        </span>
      </button>

      {/* Detalle expandido */}
      {expanded && (
        <div className="border-t bg-muted/20 p-3 space-y-2">
          {changes.length > 0 ? (
            <ul className="flex flex-col gap-1">
              {changes.slice(0, 30).map((c) => (
                <li
                  key={c.field}
                  className="flex flex-wrap items-center gap-1.5"
                >
                  <span className="font-medium">{labelFor(c.field)}:</span>
                  <span
                    className={
                      c.before !== undefined
                        ? "text-muted-foreground line-through"
                        : "text-muted-foreground"
                    }
                  >
                    {formatValue(c.before)}
                  </span>
                  <ArrowRight size={10} className="text-muted-foreground" />
                  <span className="font-mono text-foreground">
                    {formatValue(c.after)}
                  </span>
                </li>
              ))}
              {changes.length > 30 && (
                <li className="text-[10px] text-muted-foreground italic">
                  +{changes.length - 30} cambios más
                </li>
              )}
            </ul>
          ) : (
            <p className="text-[11px] text-muted-foreground italic">
              Evento sin cambios de campos (acción registrada como marca).
            </p>
          )}
          {motivo && (
            <div className="flex items-start gap-1 text-[11px] italic text-muted-foreground">
              <span className="font-medium not-italic">Motivo completo:</span>
              <span>{motivo}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
